
import { styled } from '@material-ui/core';
import ArchiveIcon from '@material-ui/icons/Archive';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { Collection } from '@type-r/models';
import { useIO, useLinked } from '@type-r/react';
import { globalStore } from 'api';
import { Activity } from 'api/activity';
import { Document } from "api/uploads";
import { AllActivitySection } from 'common/activities/activitySection';
import { asCurrency, asDate, asDateTime, asTimelessDate, CollapsableSection, Column, Columns, FormInput, FormLabel, InlineTable, Link, Loading, PopoverValue, PopupIODialog, TableCell, TableHeader, TableRow, TopSection } from 'lqx-react';
import * as React from 'react';
import { execBookPayment, Payment, Reconciliation } from 'src/app/pages/inmatch/api';
import { RemittanceAdviceInfo } from '../../matching/unreconciled/payment-details';
import { MatchedInvoices, UnfundedInvoices } from '../../matching/unreconciled/potential-matches';

export const PaymentInformation = ({ payment, reconciliations, activities, uploads, actions } : {
    payment : Payment,
    reconciliations: Collection<Reconciliation>,
    activities :  Collection<Activity>,
    uploads : Collection<Document>,
    actions? : React.ReactNode,
}) => {

    const onBookPayment = async () => {
        await Promise.all([
            activities.fetch({params: {assetId: payment.id, assetType: 'PaymentInfo'}})
        ]);
    }

    return (
        <>
         <TopSection id="payment-information" title="Payment Information">
            {payment.remittanceAdviceId ? 
                <RemittanceAdviceInfo payment={ payment } expanded disabled showExport 
                    right={actions} /> :
                undefined
            }
            <CollapsableSection id="payment-info" title="Details"
                right={ !payment.remittanceAdviceId ? actions : undefined }>
                <Columns>
                    <Column>
                        <FormInput label="Payment Amount" value={asCurrency(payment.cashReceived, payment.currency)} />
                        <FormInput label="Bank Reference No" value={payment.paymentDetail.accountServicerReference} />
                        <FormInput label="Value Date" value={asTimelessDate(payment.valueDate)} />
                        <FormInput label="Currency" value={payment.currency} />
                        <FormInput label="Instument" value="-" />
                    </Column>
                    <Column>
                        <FormInput label="InBlock Transaction ID" value={payment.id} />
                        <FormInput label="Customer Transaction ID" value={payment.paymentDetail.endToEndId} />
                        <FormInput label="Statement Date" value={asTimelessDate(payment.statementDate)} />
                        <FormInput label="Exchange Rate" value={payment.paymentDetail.exchangeRate} />
                        <FormInput label="Bank Transaction Code" value={payment.baiCode} />
                    </Column>
                </Columns>
            </CollapsableSection>
        </TopSection>

        <TopSection id="bank-reference" title="Bank Reference">
            <CollapsableSection id="bank-reference" title="Details">
                <BreakWordParagraph>{payment.paymentDetail.remittanceInfoUnstructured}</BreakWordParagraph>
                <BreakWordParagraph>{payment.paymentDetail.additionalEntryInfo}</BreakWordParagraph>
            </CollapsableSection>
            {reconciliations && reconciliations.length ? (
                <MatchDetails reconciliations={reconciliations} onBookPayment={onBookPayment} currency={payment.currency} 
                    conversationId={payment.conversationId} />
            ) : null
            }
        </TopSection>

        <TopSection id="customer" title="Payor">
            <CollapsableSection id="customer-profile" title="Profile">
                <Columns>
                    <Column>
                        <FormInput label="Payor" value={payment.customerName} />
                        {/* <FormInput label="Id" value={payment.conversation.customer.externalRelationshipId} /> */}
                    </Column>
                    <Column>
                        {/* <FormInput label="Global Payor" value={payment.conversation.customer.parentName} /> */}
                        <FormInput label="Payor Agent" value={payment.payorAgentPlatformCompanyId ? payment.payorAgent.name : ''} />
                    </Column>
                </Columns>
            </CollapsableSection>
            <CollapsableSection id="customer-bank-details" title="Bank Details">
                <Columns>
                    <Column>
                        <FormInput label="Bank Name" value={payment.paymentDetail.debtorBankName} />
                        <FormInput label="SWIFT Code" value="-" />
                    </Column>
                    <Column>
                        <FormInput label="Bank Account No" value={payment.paymentDetail.debtorAccount} />
                    </Column>
                </Columns>
            </CollapsableSection>
        </TopSection>

        <TopSection id="supplier" title="Payee">
            <CollapsableSection id="supplier-profile" title="Profile">
                <Columns>
                    <Column>
                        <FormInput label="Payee" value={payment.supplierName} />
                        {/* <FormInput label="Co #" value={payment.conversation.supplier.externalId} /> */}
                    </Column>
                    <Column>
                        {/* <FormInput label="Global Payee" value={payment.conversation.supplier.parentName} />
                        <FormInput label="Legal Entity ID" value={payment.conversation.supplier.companyLegalEntityId} /> */}
                    </Column>
                </Columns>
            </CollapsableSection>
            <CollapsableSection id="supplier-bank-details" title="Bank Details">
                <Columns>
                    <Column>
                        <FormInput label="Bank Name" value={payment.paymentDetail.creditorBankName} />
                        <FormInput label="SWIFT Code" value="-" />
                    </Column>
                    <Column>
                        <FormInput label="Bank Account No" value={payment.paymentDetail.creditorAccount} />
                    </Column>
                </Columns>
            </CollapsableSection>
        </TopSection>

        <TopSection title="Additional Information" id="additional-information">
            <CollapsableSection
                id="documents"
                title="Documents"
            >
                <InlineTable noBorders>
                    { uploads.map( file =>
                        <TableRow key={file.id}>
                            <TableCell>
                                <Link onClick={() => file && file.download()}>{file.file_name}</Link> - { file.file_comments }
                            </TableCell>
                            <TableCell className="fit-width">
                                <FormLabel>{ asDateTime(file.upload_timestamp) }</FormLabel>
                            </TableCell>
                        </TableRow>
                        )
                    }
                </InlineTable>
            </CollapsableSection>

            {activities ?
                <AllActivitySection activities={activities} uploads={uploads} currency={payment.currency} /> : undefined }
        </TopSection>
        </>
    )
}

const MatchDetails = ({reconciliations, onBookPayment, currency, conversationId } : {
    reconciliations: Collection<Reconciliation>,
    onBookPayment: () => Promise<void>,
    currency: string,
    conversationId: number,
}) => {

    return (
        <CollapsableSection id="match-details" title="Match Details">
            <InlineTable
                head={
                    <TableHeader>
                        <TableCell align="right">Invs</TableCell>
                        <TableCell>Match Date</TableCell>
                        <TableCell align="right">Cash ID</TableCell>
                        <TableCell align="right">Amt Applied(USD)</TableCell>
                        <TableCell align="right">Amt Remaining</TableCell>
                        <TableCell align="right">Processing Fee</TableCell>
                        <TableCell align="right">FX Gain/Loss</TableCell>
                        <TableCell align="right">Credit Note</TableCell>
                        <TableCell align="right">Amt Applied(invoices)</TableCell>
                        <TableCell align="right">Discount (invoices)</TableCell>
                        <TableCell align="right">Write Off(invoices)</TableCell>
                        <TableCell />
                    </TableHeader>
                }
            >
                {reconciliations.map(reconciliation =>
                    <ReconciliationRow reconciliation={reconciliation} onBookPayment={onBookPayment} currency={currency} conversationId={reconciliation.conversationId} />
                )}
            </InlineTable>
        </CollapsableSection>
    )
}

const ReconciliationRow = ({reconciliation, onBookPayment, currency, conversationId } : {
    reconciliation: Reconciliation,
    onBookPayment: () => Promise<void>,
    currency: string,
    conversationId: number
}) => {

    const bookPayment = async () => {
        await execBookPayment(reconciliation.id);

        await onBookPayment();
    }

    const unfundedMatches = reconciliation.matches.filter(inv => !inv.invoiceId && !!inv.invoiceNumber);

    return (
        <TableRow key={reconciliation.cid}>            
            <TableCell>
                <PopoverValue label={<>{reconciliation.matches.length} <MoreHorizIcon/></>} anchorAlign="left" transformAlign="left">
                    <MatchedInvoices matches={ reconciliation.matches } disabled showExport currency={ currency } />

                    { 
                        unfundedMatches && unfundedMatches.length ? 
                            <UnfundedInvoices matches={ unfundedMatches } currency={reconciliation.currency} remitanceAdviceId={unfundedMatches[0].remittanceAdviceId} disabled />
                        : undefined 
                    }
                </PopoverValue>
            </TableCell>
            <TableCell>{asDate(reconciliation.createdOn)}</TableCell>
            <TableCell>{reconciliation.id}</TableCell>
            <TableCell>{asCurrency(reconciliation.cashApplied, currency)}</TableCell>
            <TableCell>{reconciliation.deductables && asCurrency(reconciliation.deductables.overpayment, currency)}</TableCell>
            <TableCell>{reconciliation.deductables && asCurrency(reconciliation.deductables.processingFees, currency)}</TableCell>
            <TableCell>{reconciliation.deductables && asCurrency(reconciliation.deductables.fxGainLoss, currency)}</TableCell>
            <TableCell>-</TableCell>
            <TableCell>{asCurrency(reconciliation.matches.reduce((acc : number, x) => acc + x.appliedAmount + x.discountAmount + x.writeOff, 0), currency)}</TableCell>
            <TableCell>{reconciliation.deductables && asCurrency(reconciliation.deductables.discount, currency)}</TableCell>
            <TableCell>{reconciliation.deductables && asCurrency(reconciliation.deductables.writeOff, currency)}</TableCell>
            <TableCell>{reconciliation.ableToBook ? <BookPaymentButton onClick={bookPayment} conversationId={conversationId} /> : undefined }</TableCell>
        </TableRow>
    )
}

export const BreakWordParagraph = styled('p')({
    overflowWrap: 'break-word',
});

const BookPaymentButton = ({ onClick, conversationId }) => {

    const $isOpen = useLinked( false );

    return (
    <>
        <ArchiveIcon onClick={ $isOpen.true } />
    {$isOpen.value ? <GenerateFeedConfirmationDialog $isOpen={$isOpen} onClick={onClick} conversationId={conversationId} /> : null }
    </>)
};

export const GenerateFeedConfirmationDialog = ({ $isOpen, onClick, conversationId }) => {

    const title = "Book payment?";
    const successMsg = "Payment booked";

    return <PopupIODialog
            title={ title }
            $isOpen={ $isOpen }
            actionLabel="Yes"
            success={ successMsg }
            doIO={ onClick }
            hideTitleOnSuccess
        >
        </PopupIODialog>;
};
